const OFFLINE_CACHE_VERSION = 1;
const MAP_DESCRIPTOR_PREFIX = 'mindmappi:offline-map:v1:';
const DATABASE_INDEX_PREFIX = 'mindmappi:offline-yjs-databases:v1:';
const DATABASE_NAME_PREFIX = 'mindmappi-yjs-v1';
const MAP_ROLES = new Set(['owner', 'editor', 'viewer']);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

function descriptorKey(userId, mindmapId) {
  if (!isUuid(userId) || !isUuid(mindmapId)) return null;
  return `${MAP_DESCRIPTOR_PREFIX}${userId}:${mindmapId}`;
}

function databaseIndexKey(userId) {
  return isUuid(userId) ? `${DATABASE_INDEX_PREFIX}${userId}` : null;
}

export function buildOfflineYjsDatabaseName(userId, mindmapId) {
  if (!isUuid(userId) || !isUuid(mindmapId)) return null;
  return `${DATABASE_NAME_PREFIX}:${userId}:${mindmapId}`;
}

export function cacheOfflineMapDescriptor(userId, remoteMap) {
  if (typeof window === 'undefined' || !remoteMap) return false;
  const mindmapId = remoteMap.mindmapId;
  const key = descriptorKey(userId, mindmapId);
  if (
    !key
    || !MAP_ROLES.has(remoteMap.accessRole)
    || (
      remoteMap.ownerUserId !== null
      && remoteMap.ownerUserId !== undefined
      && !isUuid(remoteMap.ownerUserId)
    )
  ) {
    return false;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify({
      v: OFFLINE_CACHE_VERSION,
      mindmapId,
      ownerUserId: remoteMap.ownerUserId || null,
      accessRole: remoteMap.accessRole,
      cachedAt: new Date().toISOString(),
    }));
    return true;
  } catch {
    return false;
  }
}

export function readOfflineMapDescriptor(userId, mindmapId) {
  if (typeof window === 'undefined') return null;
  const key = descriptorKey(userId, mindmapId);
  if (!key) return null;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key));
    if (
      parsed?.v !== OFFLINE_CACHE_VERSION
      || parsed.mindmapId !== mindmapId
      || !MAP_ROLES.has(parsed.accessRole)
      || (
        parsed.ownerUserId !== null
        && !isUuid(parsed.ownerUserId)
      )
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return {
      mindmapId: parsed.mindmapId,
      ownerUserId: parsed.ownerUserId,
      accessRole: parsed.accessRole,
      nodes: [],
      relationships: [],
      root: null,
      selectedId: null,
      offlineCache: true,
    };
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
}

export function registerOfflineYjsDatabase(userId, mindmapId) {
  if (typeof window === 'undefined') return null;
  const databaseName = buildOfflineYjsDatabaseName(userId, mindmapId);
  const indexKey = databaseIndexKey(userId);
  if (!databaseName || !indexKey) return null;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(indexKey) || '[]');
    const names = Array.isArray(parsed)
      ? parsed.filter((name) => typeof name === 'string')
      : [];
    if (!names.includes(databaseName)) {
      names.push(databaseName);
      window.localStorage.setItem(indexKey, JSON.stringify(names));
    }
  } catch {
    // IndexedDB persistence can continue even when the cleanup index is full.
  }
  return databaseName;
}

export function clearOfflineAccountData(userId) {
  if (typeof window === 'undefined' || !isUuid(userId)) return;
  const indexKey = databaseIndexKey(userId);
  const databaseNames = new Set();

  try {
    const parsed = JSON.parse(window.localStorage.getItem(indexKey) || '[]');
    if (Array.isArray(parsed)) {
      parsed
        .filter((name) => typeof name === 'string')
        .forEach((name) => databaseNames.add(name));
    }
  } catch {
    // Continue with descriptor cleanup and database enumeration.
  }

  const keysToRemove = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        key === indexKey
        || key?.startsWith(`${MAP_DESCRIPTOR_PREFIX}${userId}:`)
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Continue with best-effort IndexedDB cleanup.
  }

  const deleteKnownDatabases = () => {
    if (!window.indexedDB) return;
    databaseNames.forEach((name) => {
      if (name.startsWith(`${DATABASE_NAME_PREFIX}:${userId}:`)) {
        window.indexedDB.deleteDatabase(name);
      }
    });
  };

  if (typeof window.indexedDB?.databases === 'function') {
    window.indexedDB.databases()
      .then((databases) => {
        for (const database of databases) {
          if (
            typeof database?.name === 'string'
            && database.name.startsWith(`${DATABASE_NAME_PREFIX}:${userId}:`)
          ) {
            databaseNames.add(database.name);
          }
        }
        deleteKnownDatabases();
      })
      .catch(deleteKnownDatabases);
    return;
  }
  deleteKnownDatabases();
}

